/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDSnackbar from 'components/MDSnackbar';
import MDSelect from 'components/MDSelect';
import MDCheckbox from 'components/MDCheckbox';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initAttr = {
  id: '',
  barcode_id: '',
  branch_id: '',
  price: '',
  cost_fix: '',
};

function ProductBranch({ parent, reload }) {
  const mod = 'product_branch';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [cboBranch, setCboBranch] = useState([]);
  const [notify, setNotify] = useState(null);
  const [field, setField] = useState({});
  const [editBranch, setEditBranch] = useState(false);
  const [isNow, setIsNow] = useState(false);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = () => {
    setLoading(true);
    FetchData('POST', `/${mod}`, app.token, {
      id: parent.id,
    }).then((result) => {
      if (result.status === 'ok') {
        setLoading(false);

        setList(result.list);
        setCboBranch(result.branch);
        setField(result.parent);
      }
    });
  };

  const open = () => {
    setAttr(initAttr);
    setIsNow(false);
    setModalOpen(true);
    setEditBranch(false);

    view();
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.branch_id, 1)) return;
    if (!Func.verifyLength(attr.price, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    if (attr.branch_id === 'ALL') setEditBranch(true);

    setConfirm(false);
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      barcode_id: parent.id,
      branch_id: attr.branch_id,
      price: attr.price,
      isNow: isNow,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        setAttr(initAttr);
        view();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view();
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const delAll = () => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/delall`, app.token, {
          id: parent.id,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view();
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const profit = (item) => {
    const price = item.price * 1;
    const cost = parent.cost_fix * 1;
    const cost_markup = parent.cost_markup * 1;
    const cost_sell = cost + (cost * cost_markup) / 100;
    // const profit = cost_sell === 0 ? 100 : (price / cost_sell) * 100 - 100;
    const profit = cost_sell === 0 ? 100 : ((price - cost_sell) * 100) / price;

    return profit;
  };

  const costSell = () => {
    const cost = parent.cost_fix * 1;
    const cost_markup = parent.cost_markup * 1;
    const cost_sell = cost + (cost * cost_markup) / 100;

    return cost_sell;
  };

  const btnEditMain = () => {
    return (
      <Tooltip title={t(`element.edit`)}>
        <MDButton
          variant='text'
          color='success'
          size='small'
          iconOnly
          onClick={() => {
            setAttr({ ...initAttr, price: field.price, branch_id: 'ALL' });
          }}
        >
          <Icon>border_color</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const btnEdit = (item) => {
    return (
      <Tooltip title={t(`element.edit`)}>
        <MDButton
          variant='text'
          color='success'
          size='small'
          iconOnly
          onClick={() => {
            setAttr({
              ...initAttr,
              price: item.price,
              branch_id: item.branch_id,
            });
          }}
        >
          <Icon>border_color</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const btnDel = (item) => {
    if (!app.perm.del) return null;

    return (
      <Tooltip title={t(`element.delete`)}>
        <MDButton variant='text' color='error' size='small' iconOnly onClick={() => del(item)}>
          <Icon>delete</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const btnDelAll = () => {
    if (!app.perm.del) return null;

    return (
      <Tooltip title={t(`element.delete`)}>
        <MDButton variant='text' color='error' size='small' iconOnly onClick={() => delAll()}>
          <Icon>delete</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <MDInput
              label='สาขา'
              variant='standard'
              fullWidth
              value={attr.branch_id}
              defaultValue='ALL'
              select
              onChange={(e) => setAttr({ ...attr, branch_id: e.target.value })}
            >
              <MenuItem value='ALL'>สาขาอื่น ๆ</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} md={5}>
            <MDInput
              label='ราคา'
              onFocus={(e) => e.target.select()}
              onChange={(e) => setAttr({ ...attr, price: e.target.value })}
              value={attr.price}
              variant='standard'
              type='number'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDTypography variant='body'>บาท</MDTypography>
                  </InputAdornment>
                ),
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
        </Grid>
        <MDBox mt={1} display='flex' justifyContent='flex-end'>
          <MDCheckbox
            label='มีผลทันที'
            checked={isNow}
            onChange={(e) => setIsNow(e.target.checked)}
          />
        </MDBox>
        <MDBox mt={2}>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <Grid container>
              <Grid item xs={12} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='info' onClick={addConfirm}>
                  <Icon>save</Icon>&nbsp;{t(`element.save`)}
                </MDButton>
              </Grid>
            </Grid>
          )}
        </MDBox>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox className='card'>
        <TableContainer sx={{ boxShadow: 0, minHeight: 300, maxHeight: 300 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell align='center'>No.</TableCell>
                <TableCell>สาขา</TableCell>
                <TableCell align='right'>ปันทุน</TableCell>
                <TableCell align='right' className='nowrap'>
                  Mark up
                </TableCell>
                <TableCell align='right'>ทุนขาย</TableCell>
                <TableCell align='right'>ขาย</TableCell>
                <TableCell align='right'>ผลกำไร</TableCell>
                <TableCell width={10}>&nbsp;</TableCell>
                <TableCell align='center' width={10}>
                  {btnDelAll()}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align='center' width='50'>
                  1
                </TableCell>
                <TableCell align='left'>สาขาอื่น ๆ</TableCell>
                <TableCell align='right'>
                  <b>{Func.numberDisplay(field.cost_fix, 2)}</b>
                </TableCell>
                <TableCell align='right'>
                  <b>{Func.numberDisplay(field.cost_markup)}%</b>
                </TableCell>
                <TableCell align='right'>
                  <b>{Func.numberDisplay(costSell(), 2)}</b>
                </TableCell>
                <TableCell align='right'>
                  <b>{Func.numberDisplay(field.price, 2)}</b>
                </TableCell>
                <TableCell align='right' className='color-danger'>
                  <b>{Func.numberDisplay(profit(field))}%</b>
                </TableCell>
                <TableCell align='center'>{btnEditMain()}</TableCell>
                <TableCell>&nbsp;</TableCell>
              </TableRow>
              {list.map((row, i) => (
                <TableRow key={row.id}>
                  <TableCell align='center'>{i + 2}</TableCell>
                  <TableCell align='left'>{row.branch_name}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(parent.cost_fix, 2)}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(parent.cost_markup)}%</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(costSell(), 2)}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.price, 2)}</TableCell>
                  <TableCell align='right' className='color-danger'>
                    {Func.numberDisplay(profit(row))}%
                  </TableCell>
                  <TableCell align='center'>{btnEdit(row)}</TableCell>
                  <TableCell align='center'>{btnDel(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const btnOpen = () => {
    return (
      <Tooltip title='ราคาขาย'>
        <MDButton
          variant='text'
          color='success'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'inline-block',
            textAlign: 'right',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
          onClick={open}
        >
          {Func.numberDisplay(parent.price, 2)} ฿
        </MDButton>
      </Tooltip>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='md'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => {
          setModalOpen(false);
          if (editBranch) reload();
        }}
      >
        <DialogTitle>
          <MDBox className='cut' sx={{ width: 400 }}>
            ราคาขาย : {parent.code}
          </MDBox>
          <IconButton
            aria-label='close'
            onClick={() => {
              setModalOpen(false);
              if (editBranch) reload();
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {renderInfo()}
          {renderList()}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      {btnOpen()}
      {renderOpen()}

      {renderLoading()}
      {notify}
    </div>
  );
}

ProductBranch.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    barcode: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    cost: PropTypes.string.isRequired,
    cost_fix: PropTypes.string.isRequired,
    cost_sell: PropTypes.string.isRequired,
    cost_markup: PropTypes.string.isRequired,
  }).isRequired,
  reload: PropTypes.func.isRequired,
};

export default ProductBranch;
